import {
  ChangeDetectionStrategy,
  Component,
  computed,
  type ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer, type SafeResourceUrl } from '@angular/platform-browser';

import { AuthService } from '@core/auth/services/auth.service';
import { LanguageService } from '@core/services/language.service';

import { type DynamicAction } from '@shared/registry';

import { environment } from '../../../../environments/environment';

import type { ConsentIframeConfig } from './consent-iframe.types';

@Component({
  selector: 'app-consent-iframe',
  templateUrl: './consent-iframe.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block overflow-hidden flex-1',
    '(window:message)': 'onIframeMessage($event)',
  },
})
export class ConsentIframe {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly authService = inject(AuthService);
  private readonly languageService = inject(LanguageService);

  private readonly iframeRef = viewChild<ElementRef<HTMLIFrameElement>>('iframe');

  readonly config = input.required<ConsentIframeConfig>();

  readonly action = output<DynamicAction>();

  private readonly token = signal<string | undefined>(undefined);

  protected readonly iframeUrl = computed<SafeResourceUrl>(() => {
    const key = this.config().key;
    const lang = this.languageService.shortLanguage();
    const tenantId = this.config().tenantId;
    const tenantParam = tenantId ? `&tenantId=${encodeURIComponent(tenantId)}` : '';
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `${environment.consentAppUrl}/consent/${key}?lang=${lang}${tenantParam}`,
    );
  });

  constructor() {
    this.setAuthToken();
  }

  protected onIframeMessage(event: MessageEvent): void {
    const allowedOrigins = [environment.consentAppUrl, window.location.origin];
    if (!allowedOrigins.includes(event.origin)) {
      return;
    }

    if (event.origin === window.location.origin && event.data.type === 'session-refresh') {
      this.setAuthToken();
      this.sendAuthTokenToIframe(true);
      return;
    }

    switch (event.data.type) {
      case 'signed':
        this.onConsentSigned(event.data.documentIds);
        break;
      case 'ready':
        this.sendAuthTokenToIframe();
        break;
    }
  }

  private onConsentSigned(documentIds: string[]): void {
    const { signedAction, signedActionDelay } = this.config();
    const actionToEmit: DynamicAction = signedAction
      ? { ...signedAction, data: { ...(signedAction.data as object), documentIds } }
      : { id: 'documentsSigned', data: documentIds };

    if (signedActionDelay) {
      setTimeout(() => this.action.emit(actionToEmit), signedActionDelay);
      return;
    }
    this.action.emit(actionToEmit);
  }

  private sendAuthTokenToIframe(isRefresh = false): void {
    const iframe = this.iframeRef()?.nativeElement;
    if (!iframe?.contentWindow) return;

    iframe.contentWindow.postMessage(
      {
        type: isRefresh ? 'refresh' : 'auth',
        token: this.token(),
        tenantId: this.config().tenantId,
        timestamp: Date.now(),
      },
      environment.consentAppUrl,
    );
  }

  private async setAuthToken(): Promise<void> {
    this.token.set(await this.authService.getToken());
  }
}
